import * as React from "react"
import { Search, Filter, X, Check, ChevronDown } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { useSearchParams, useNavigate, useLocation } from "react-router-dom"
import type { GapClientConfig } from "@/types/gap-client"

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = React.useState<T>(value)

  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])

  return debouncedValue
}

interface FilterDropdownProps {
  icon: React.ReactNode
  label: string
  options: { id: string; name: string }[]
  selected: Set<string>
  onSelectionChange: (selected: Set<string>) => void
}

function FilterDropdown({ icon, label, options, selected, onSelectionChange }: FilterDropdownProps) {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState("")

  const filteredOptions = options.filter((opt) => opt.name.toLowerCase().includes(search.toLowerCase()))

  const toggleOption = (id: string) => {
    const newSelection = new Set(selected)
    if (newSelection.has(id)) {
      newSelection.delete(id)
    } else {
      newSelection.add(id)
    }
    onSelectionChange(newSelection)
  }

  const selectAll = () => {
    onSelectionChange(new Set(options.map((o) => o.id)))
  }

  const clearAll = () => {
    onSelectionChange(new Set())
  }

  const hasSelection = selected.size > 0
  const selectedNames = options.filter((o) => selected.has(o.id)).map((o) => o.name)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={`${
            hasSelection
              ? "bg-primary/20 border-primary/50 text-primary hover:bg-primary/30"
              : "bg-card border-border text-muted-foreground hover:bg-secondary hover:text-foreground"
          }`}
        >
          {icon}
          {hasSelection ? (
            <span className="truncate max-w-[120px]">
              {selectedNames.length === 1 ? selectedNames[0] : `${selectedNames.length} selected`}
            </span>
          ) : (
            `All ${label}`
          )}
          <ChevronDown className="h-4 w-4 ml-1 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0 bg-popover border-border" align="start">
        {/* Search input */}
        <div className="p-2 border-b border-border">
          <Input
            placeholder={`Search ${label.toLowerCase()}...`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 bg-background border-input text-foreground placeholder:text-muted-foreground"
          />
        </div>

        {/* Select all / Clear actions */}
        <div className="flex items-center justify-between px-2 py-1.5 border-b border-border">
          <Button
            variant="ghost"
            size="sm"
            onClick={selectAll}
            className="h-7 text-xs text-muted-foreground hover:text-foreground hover:bg-secondary"
          >
            Select All
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAll}
            className="h-7 text-xs text-muted-foreground hover:text-foreground hover:bg-secondary"
          >
            Clear
          </Button>
        </div>

        {/* Options list */}
        <div className="max-h-48 overflow-y-auto py-1">
          {filteredOptions.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No results found</p>
          ) : (
            filteredOptions.map((option) => (
              <div
                key={option.id}
                className="flex items-center gap-2 px-2 py-1.5 hover:bg-secondary cursor-pointer"
                onClick={() => toggleOption(option.id)}
              >
                <Checkbox
                  checked={selected.has(option.id)}
                  className="border-input data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                />
                <span className="text-sm text-muted-foreground">{option.name}</span>
                {selected.has(option.id) && <Check className="h-4 w-4 ml-auto text-primary" />}
              </div>
            ))
          )}
        </div>

        {/* Apply button */}
        <div className="p-2 border-t border-border">
          <Button
            size="sm"
            onClick={() => setOpen(false)}
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export interface SearchFiltersState {
  search: string
  clients: Set<string>
  venues: Set<string> // Kept for data model compatibility, but not used in UI
  activeOnly: boolean // Kept for data model compatibility, but not used in UI
}

interface SearchFiltersProps {
  onChange?: (filters: SearchFiltersState) => void
  clients?: GapClientConfig[]
}

export function SearchFilters({ onChange, clients = [] }: SearchFiltersProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const searchInputRef = React.useRef<HTMLInputElement>(null)
  const isInternalUpdate = React.useRef(false)

  const [searchQuery, setSearchQuery] = React.useState(searchParams.get("q") || "")
  const [selectedClients, setSelectedClients] = React.useState<Set<string>>(() => {
    const clientsParam = searchParams.get("clients")
    return clientsParam ? new Set(clientsParam.split(",")) : new Set()
  })

  // Wrapper for setSelectedClients that marks updates as internal
  const setSelectedClientsInternal = React.useCallback((newSelection: Set<string>) => {
    isInternalUpdate.current = true
    setSelectedClients(newSelection)
  }, [])

  const debouncedSearch = useDebounce(searchQuery, 150)

  // Sync with URL params only when the URL changes (e.g., navigation from clients page).
  // Do NOT depend on searchQuery/selectedClients so we don't overwrite user input on every keystroke.
  React.useEffect(() => {
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false
      return
    }

    const urlSearch = searchParams.get("q") || ""
    const urlClients = searchParams.get("clients")
    const urlClientsSet: Set<string> = urlClients ? new Set<string>(urlClients.split(",")) : new Set<string>()

    setSearchQuery(urlSearch)
    setSelectedClients(urlClientsSet)
  }, [searchParams])

  React.useEffect(() => {
    const params = new URLSearchParams()
    if (debouncedSearch) params.set("q", debouncedSearch)
    if (selectedClients.size > 0) params.set("clients", Array.from(selectedClients).join(","))

    const newUrl = params.toString() ? `${location.pathname}?${params.toString()}` : location.pathname
    const currentUrl = `${location.pathname}${location.search}`
    
    // Only update URL if it actually changed
    if (newUrl !== currentUrl) {
      isInternalUpdate.current = true
      navigate(newUrl, { replace: true })
    }

    onChange?.({
      search: debouncedSearch,
      clients: selectedClients,
      venues: new Set(), // Empty set for data model compatibility
      activeOnly: false, // Always false for data model compatibility
    })
  }, [debouncedSearch, selectedClients, location.pathname, location.search, navigate, onChange])

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "/" || (e.metaKey && e.key === "k")) {
        e.preventDefault()
        searchInputRef.current?.focus()
      }
      if (e.key === "Escape" && document.activeElement === searchInputRef.current) {
        setSearchQuery("")
        searchInputRef.current?.blur()
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [])

  const clearSearch = () => {
    isInternalUpdate.current = true
    setSearchQuery("")
    searchInputRef.current?.focus()
  }

  const removeClientFilter = (clientId: string) => {
    const newSelection = new Set(selectedClients)
    newSelection.delete(clientId)
    setSelectedClientsInternal(newSelection)
  }

  const clearAllFilters = () => {
    isInternalUpdate.current = true
    setSearchQuery("")
    setSelectedClientsInternal(new Set())
  }

  const hasActiveFilters = selectedClients.size > 0

  // Convert GapClientConfig[] to filter options format
  const clientOptions = React.useMemo(() => {
    return clients.map((client) => ({
      id: client.clientId,
      name: client.clientId, // Use clientId as display name
    }))
  }, [clients])

  const selectedClientNames = clientOptions.filter((c) => selectedClients.has(c.id))

  return (
    <div className="space-y-3">
      {/* Search Input */}
      <div className="relative max-w-lg">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          ref={searchInputRef}
          placeholder="Search devices by ID or client..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="h-11 pl-10 pr-20 bg-background border-border text-foreground placeholder:text-muted-foreground focus:ring-ring focus:border-ring"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
          {searchQuery && (
            <Button
              variant="ghost"
              size="icon"
              onClick={clearSearch}
              className="h-6 w-6 text-muted-foreground hover:text-foreground hover:bg-secondary"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Clear search</span>
            </Button>
          )}
          <kbd className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 text-xs text-muted-foreground bg-secondary rounded border border-border">
            <span className="text-xs">⌘</span>K
          </kbd>
        </div>
      </div>

      {/* Filter Dropdowns */}
      <div className="flex flex-wrap items-center gap-3">
        <FilterDropdown
          icon={<Filter className="h-4 w-4 mr-2" />}
          label="Clients"
          options={clientOptions}
          selected={selectedClients}
          onSelectionChange={setSelectedClientsInternal}
        />
      </div>

      {/* Active Filter Pills */}
      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2">
          {selectedClientNames.map((client) => (
            <span
              key={client.id}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm bg-secondary text-secondary-foreground hover:bg-secondary/80 cursor-pointer"
              onClick={() => removeClientFilter(client.id)}
            >
              Client: {client.name}
              <X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
            </span>
          ))}

          <button onClick={clearAllFilters} className="text-sm text-primary hover:text-primary/80 hover:underline ml-2">
            Clear all filters
          </button>
        </div>
      )}
    </div>
  )
}
